import { useState } from "react";
import { useAuth } from "../../contexts/AuthContext.js";
import { AuthCard, AuthField, AuthSubmit, AuthError } from "./AuthShell.js";

export default function SignIn() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await signIn(email.trim().toLowerCase(), password);
      // On success the AuthProvider's session load flips the gate to the
      // dashboard; nothing to navigate to.
    } catch (err) {
      setError(humanise(err));
      setPending(false);
    }
  }

  return (
    <AuthCard
      title="Sign in"
      subtitle="Sign in to explore your dashboard."
      // Admin-provisioned accounts — no self-service sign-up (WS-D D2′). A quiet
      // hint rather than a sign-up link.
      footer="Access is provisioned by your administrator."
    >
      <form onSubmit={onSubmit}>
        <AuthError message={error} />
        <AuthField
          label="Email"
          name="email"
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
        />
        <AuthField
          label="Password"
          name="password"
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          revealable
        />
        <AuthSubmit label="Sign in" pending={pending} />
      </form>
    </AuthCard>
  );
}

/** Map a Cognito/Amplify sign-in error to a friendly message. Exported for a
 *  pure unit test (the DOM reject-path is flaky under vitest's unhandled-reject
 *  detector). */
export function humanise(err: unknown): string {
  const name = (err as { name?: string })?.name ?? "";
  if (name === "NotAuthorizedException") return "Wrong email or password.";
  if (name === "UserNotConfirmedException") return "This account isn't active yet — contact your administrator.";
  if (name === "UserNotFoundException") return "No account found for this email.";
  if (name === "PasswordResetRequiredException")
    return "Your password needs resetting — contact your administrator.";
  return (err as { message?: string })?.message ?? "Sign-in failed. Please try again.";
}
